import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Building2, Settings2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';

/**
 * Phase 2 Agency: dashboard summary card shown only when the current user
 * is an agency owner. Renders nothing for individual / SME users.
 *
 * v3.2 polish (21/05/26):
 *   - Dropped the legacy "X recruiter" segment (v3.1 billing unit; the
 *     v3.2 model bills per-company, not per-recruiter).
 *   - Added tier labels for agency_medium and agency_enterprise.
 *   - "Manage" CTA now points at /profile?tab=billing (the new user-level
 *     subscription page; the legacy /settings?tab=subscription still works
 *     via redirect but is being deprecated).
 */
const TIER_LABEL_KEY: Record<string, string> = {
  agency_starter: 'dashboard.agency.tier.starter',
  agency_medium: 'subscription.tier.agency_medium',
  agency_professional: 'dashboard.agency.tier.pro',
  agency_enterprise: 'subscription.tier.agency_enterprise',
};

export function AgencyHeaderCard() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  if (user?.accountType !== 'agency_owner' || !user.agency) return null;
  const a = user.agency;

  const tierLabel = t(TIER_LABEL_KEY[a.agencyPlanId] ?? 'dashboard.agency.tier.starter');

  return (
    <Card className="p-4 sm:p-5 border-brand-primary/30 bg-gradient-to-r from-brand-primary/5 via-brand-accent/5 to-transparent">
      <div className="flex flex-wrap items-center gap-4 justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-10 w-10 rounded-lg bg-brand-primary/10 text-brand-primary flex items-center justify-center flex-shrink-0">
            <Building2 className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap text-sm">
              <span className="font-semibold text-foreground">{tierLabel}</span>
              <span className="text-muted-foreground">·</span>
              <span className="text-foreground">
                {t('dashboard.agency.clients', { used: a.usage.clientsUsed, cap: a.limits.agencyClients })}
              </span>
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {t('dashboard.agency.totalMonth', { amount: a.billing.totalMonthly })}
            </div>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/profile?tab=billing')}
          className="gap-2"
        >
          <Settings2 className="h-4 w-4" />
          {t('dashboard.agency.manage')}
        </Button>
      </div>
    </Card>
  );
}

export default AgencyHeaderCard;